import { Skeleton, Box, SxProps, Theme } from "@mui/material";

interface LoadingSkeletonProps {
  columns?: number;
  rows?: number;
  widths?: (string | number)[]; // widths for each row
  heights?: (string | number)[]; // heights for each row
  gap?: number;
  borderRadius?: (string | number)[]; // borderRadius for each column
  variants?: ("circle" | "rectangular" | "square")[]; // variants for each column
  sx?: SxProps<Theme>; // custom styles for the skeleton
}
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  columns = 1,
  rows = 1,
  widths = [],
  heights = [],
  gap = 2,
  borderRadius = [],
  variants = [],
  sx = {},
}) => {
  const getRowWidth = (rowIndex: number) => {
    return widths[rowIndex] || widths[widths.length - 1] || "100vh";
  };

  const getRowHeight = (rowIndex: number) => {
    return heights[rowIndex] || heights[heights.length - 1] || 20;
  };

  const getColumnBorderRadius = (colIndex: number) => {
    return borderRadius[colIndex] || borderRadius[borderRadius.length - 1] || 2; // Default borderRadius of 2px
  };

  const getColumnVariant = (colIndex: number) => {
    return variants[colIndex] || variants[variants.length - 1] || "rectangular"; // Default Variant "rectangular"
  };

  const renderSkeleton = (colIndex: number, rowIndex: number) => {
    let rowWidth = getRowWidth(rowIndex);
    let rowHeight = getRowHeight(rowIndex);
    let colBorderRadius = getColumnBorderRadius(colIndex);
    let colVariant = getColumnVariant(colIndex);

    if (colVariant === "circle") {
      rowWidth = rowHeight; // Circle: width = height
    } else if (colVariant === "square") {
      rowHeight = rowWidth; // Square: height = width
    }

    return (
      <Skeleton
        key={`${colIndex}-${rowIndex}`}
        variant={colVariant === "circle" ? "circular" : "rectangular"}
        width={rowWidth}
        height={rowHeight}
        sx={{
          borderRadius: colBorderRadius,
          minHeight: rowHeight,
          maxWidth: "100%",
          ...sx,
        }}
      />
    );
  };

  return (
    <Box sx={{ display: "grid", gap }}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box
          key={`row-${rowIndex}`}
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, .5fr)`,
            gap,
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) =>
            renderSkeleton(colIndex, rowIndex)
          )}
        </Box>
      ))}
    </Box>
  );
};

export default LoadingSkeleton;
